import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { IRiskDetailsResponse } from "@gloria-public/risk/domain/interfaces/knowledge.interface";
import { IServiceRequestTemplateSummary } from "@gloria-public/risk/domain/interfaces/risk.interface";
import { RiskService } from "@gloria-public/risk/domain/services/risk.service";
import { TranslateService } from "@ngx-translate/core";
import { ServiceRequestDialogComponent } from "@shared/components/service-request-dialog/service-request-dialog.component";
import { ServiceRequestService } from "@shared/services/service-requests.service";
import { Subject, combineLatest, distinctUntilChanged, finalize, first, startWith, takeUntil } from "rxjs";

@Component({
  selector: "app-risk-details-page",
  templateUrl: "./risk-details-page.component.html",
  styleUrls: ["./risk-details-page.component.scss"],
})
export class RiskDetailsPageComponent implements OnInit, OnDestroy {
  public riskDetails!: IRiskDetailsResponse;
  public isLoading = true;
  public riskId!: string;
  public recentlyAdded = new Set<string>();
  public loadingItems = new Set<string>();
  private cartItems$ = this.orderService.cartItems$;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private riskService: RiskService,
    private translateService: TranslateService,
    private orderService: OrderService,
    private dialog: MatDialog,
    private serviceRequestService: ServiceRequestService,
    public snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.route.paramMap,
      this.translateService.onLangChange.pipe(
        startWith({
          lang: this.translateService.currentLang,
        }),
        distinctUntilChanged((a, b) => a.lang === b.lang),
        takeUntil(this.onDestroy$)
      ),
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([params, event]) => {
        const id = params.get("riskId");

        if (!id) {
          this.router.navigate(["/risks"]);
          return;
        }

        this.riskId = id;

        this.loadRisk(event.lang);
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Gets service request IDs currently in cart
   * @return Set of service request IDs
   */
  public get serviceRequestIDS(): Set<string> {
    return this.orderService.serviceRequestIDs;
  }

  /**
   * Loads risk.
   * @param lang current language
   */
  private loadRisk(lang: string): void {
    this.isLoading = true;

    this.riskService
      .getRisk(this.riskId, lang)
      .pipe(first())
      .subscribe({
        next: (risk) => {
          if (risk) {
            this.riskDetails = risk;
          }
          this.isLoading = false;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
          this.isLoading = false;
          this.router.navigate(["/risks"]);
        },
      });
  }

  /**
   * Checks whether a service request is already in cart
   * @param serviceRequestId Service request identifier
   * @return True if item exists in cart, otherwise false
   */
  public isInCart(serviceRequestId: string): boolean {
    return this.serviceRequestIDS.has(serviceRequestId);
  }

  /**
   * Handles cart button click event
   * @param item Service request item
   * @param event Mouse event
   */
  public onCartClick(item: IServiceRequestTemplateSummary, event: MouseEvent): void {
    event.stopPropagation();
    this.toggleCart(item);
  }

  /**
   * Toggles service request in cart (add or remove)
   * @param item Service request item
   */
  public toggleCart(item: IServiceRequestTemplateSummary): void {
    const serviceRequestId = item.id;

    if (this.loadingItems.has(serviceRequestId)) return;

    this.loadingItems.add(serviceRequestId);

    const isAdd = !this.isInCart(serviceRequestId);
    const request$ = isAdd
      ? this.orderService.addToCartFlow(
          {
            serviceRequestID: serviceRequestId,
            riskTemplateID: this.riskId,
          },
          item.title
        )
      : this.orderService.removeFromCartFlow(serviceRequestId);

    request$.pipe(finalize(() => this.loadingItems.delete(serviceRequestId))).subscribe({
      next: () => {
        if (isAdd) {
          this.recentlyAdded.add(serviceRequestId);

          setTimeout(() => {
            this.recentlyAdded.delete(serviceRequestId);
          }, 3000);
        }
      },
    });
  }

  /**
   * Open service request in big
   * @param id service request identifier
   */
  public openServiceRequest(id: string): void {
    this.serviceRequestService.getServiceRequestById(id, this.translateService.currentLang).subscribe({
      next: (serviceRequest) => {
        this.dialog.open(ServiceRequestDialogComponent, {
          data: {
            response: serviceRequest,
            riskId: this.riskId,
            canPurchase: true,
            alreadyBought: false,
          },
          width: "90vw",
          maxWidth: "600px",
          maxHeight: "90vh",
        });
      },
      error: () => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });
      },
    });
  }
}
